import React from 'react';
import { FieldError } from '../../onboarding/onboarding.styled';
import { Group, GroupLegend, Hint, Part, PartInput, PartLabel, Parts, PartSelect } from './birth-date-field.styled';
import { BirthParts, cleanDay, cleanYear, MONTH_NAMES } from './birth-date-model';

export type { BirthParts } from './birth-date-model';

/**
 * DATE OF BIRTH — Day, Month, Year (plan ISR, owner decision ISR-D2).
 *
 * No calendar: three boxes that work the same with a thumb, a mouse and a
 * keyboard on every browser. The day and the year bring up the number pad on a
 * phone; the month is a list of names, so 03/04 can never be misread. The
 * parent keeps the parts and checks them with birth-date-model's checkBirth.
 *
 * `id` prefixes the three controls: `${id}-day`, `${id}-month`, `${id}-year`.
 */
export const BirthDateField: React.FC<{
  id: string;
  legend: string;
  value: BirthParts;
  onChange: (next: BirthParts) => void;
  error?: string;
  hint?: string;
  disabled?: boolean;
}> = ({ id, legend, value, onChange, error, hint, disabled }) => {
  const set = (k: keyof BirthParts, v: string) => onChange({ ...value, [k]: v });
  const described = [hint ? `${id}-hint` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') || undefined;
  const bad = !!error;

  return (
    <Group aria-describedby={described} data-birth-date={id}>
      <GroupLegend>{legend}</GroupLegend>
      <Parts>
        <Part>
          <PartLabel htmlFor={`${id}-day`}>Day</PartLabel>
          <PartInput
            id={`${id}-day`}
            inputMode="numeric"
            autoComplete="off"
            placeholder="DD"
            maxLength={2}
            value={value.day}
            onChange={e => set('day', cleanDay(e.target.value))}
            hasError={bad}
            aria-invalid={bad || undefined}
            disabled={disabled}
          />
        </Part>
        <Part>
          <PartLabel htmlFor={`${id}-month`}>Month</PartLabel>
          <PartSelect
            id={`${id}-month`}
            value={value.month}
            onChange={e => set('month', e.target.value)}
            hasError={bad}
            aria-invalid={bad || undefined}
            disabled={disabled}
          >
            <option value="">Month</option>
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={String(i + 1)}>
                {name}
              </option>
            ))}
          </PartSelect>
        </Part>
        <Part>
          <PartLabel htmlFor={`${id}-year`}>Year</PartLabel>
          <PartInput
            id={`${id}-year`}
            inputMode="numeric"
            autoComplete="off"
            placeholder="YYYY"
            maxLength={4}
            value={value.year}
            onChange={e => set('year', cleanYear(e.target.value))}
            hasError={bad}
            aria-invalid={bad || undefined}
            disabled={disabled}
          />
        </Part>
      </Parts>
      {hint ? <Hint id={`${id}-hint`}>{hint}</Hint> : null}
      <FieldError id={`${id}-error`} message={error} />
    </Group>
  );
};
